import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, MapPin, Bed, Bath, Square, Phone, Mail, ChevronLeft, Facebook, Twitter, MessageCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetailPage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        property_id: id
      });
      toast.success(t('common.messageSent'));
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(t('common.messageFailed'));
    }
  };

  if (loading) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-bone">
        <div className="max-w-7xl mx-auto px-6">
          <div className="animate-pulse">
            <div className="h-96 bg-gray-200 rounded-2xl mb-8"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-bone">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-3xl font-medium text-charcoal mb-4">Property Not Found</h1>
          <Link to="/properties">
            <Button>{t('propertyDetail.back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isRent = property.listing_type === "rent";
  const priceDisplay = isRent 
    ? `$${property.price.toLocaleString()}/mo` 
    : `$${property.price.toLocaleString()}`;

  const shareUrl = encodeURIComponent(window.location.href);
  const shareText = encodeURIComponent(`Check out this amazing property: ${property.title}`);

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone" data-testid="property-detail-page">
      <div className="max-w-7xl mx-auto px-6">
        <Link to="/properties" className="inline-flex items-center text-muted-foreground hover:text-charcoal mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('propertyDetail.back')}
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden mb-6">
              <img
                src={property.image_url}
                alt={property.title}
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute top-4 left-4 flex gap-2">
                <Badge className={`${isRent ? 'bg-accent' : 'bg-primary'} text-white`}>
                  {isRent ? t('propertyCard.forRent') : t('propertyCard.forSale')}
                </Badge>
                {property.is_featured && (
                  <Badge className="bg-charcoal text-white">{t('propertyCard.featured')}</Badge>
                )}
              </div>
              <button className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform">
                <Heart className="w-6 h-6 text-muted-foreground" />
              </button>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-soft mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-heading text-3xl font-medium text-charcoal mb-2">
                    {property.title}
                  </h1>
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-5 h-5" />
                    {property.address}, {property.city}, {property.province}
                  </p>
                </div>
                <p className="text-3xl font-heading font-semibold text-primary">
                  {priceDisplay}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 py-6 border-y border-border">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Bed className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-semibold text-charcoal">{property.bedrooms}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('propertyDetail.bedrooms')}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Bath className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-semibold text-charcoal">{property.bathrooms}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('propertyDetail.bathrooms')}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Square className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-semibold text-charcoal">{property.sqft}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('propertyCard.sqft')}</p>
                </div>
              </div>

              <div className="mt-6">
                <h2 className="font-heading text-xl font-medium text-charcoal mb-4">{t('propertyDetail.description')}</h2>
                <p className="text-muted-foreground leading-relaxed">{property.description}</p>
              </div>

              {property.features && property.features.length > 0 && (
                <div className="mt-6">
                  <h2 className="font-heading text-xl font-medium text-charcoal mb-4">{t('propertyDetail.features')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.features.map((feature, index) => (
                      <div key={`feature-${index}-${feature}`} className="flex items-center gap-2 text-muted-foreground">
                        <Check className="w-4 h-4 text-primary" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm font-medium text-charcoal mb-3">{t('propertyDetail.share')}</p>
                <div className="flex gap-3">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:opacity-80 transition-opacity">
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center hover:opacity-80 transition-opacity">
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center hover:opacity-80 transition-opacity">
                    <MessageCircle className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-soft mb-6">
              <h3 className="font-heading text-lg font-medium text-charcoal mb-4">{t('propertyDetail.contactAgent')}</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-heading text-primary">
                    {property.agent_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-charcoal">{property.agent_name}</p>
                  <p className="text-sm text-muted-foreground">Dream House Agent</p>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <a href={`tel:${property.agent_phone}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <Phone className="w-4 h-4" />
                  {property.agent_phone}
                </a>
                <a href={`mailto:${property.agent_email}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <Mail className="w-4 h-4" />
                  {property.agent_email}
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <h3 className="font-heading text-lg font-medium text-charcoal mb-4">{t('propertyDetail.sendMessage')}</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <Input placeholder={t('propertyDetail.yourName')} className="rounded-xl" value={contactForm.name} onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))} required />
                <Input type="email" placeholder={t('propertyDetail.yourEmail')} className="rounded-xl" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} required />
                <Input type="tel" placeholder={t('propertyDetail.yourPhone')} className="rounded-xl" value={contactForm.phone} onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))} />
                <Textarea placeholder={`${t('propertyDetail.message')}...`} className="rounded-xl min-h-[120px]" value={contactForm.message} onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))} required />
                <Button type="submit" className="w-full rounded-full">
                  {t('propertyDetail.send')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
